import { useState, useCallback } from "react";
import { api } from "../api/client";
import type { Receipt, ReceiptListResponse } from "../types";

export interface ReceiptFilters {
  category_id?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useReceipts() {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchReceipts = useCallback(async (filters: ReceiptFilters = {}) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(
        Object.entries(filters)
          .filter(([, v]) => v !== undefined && v !== "")
          .map(([k, v]) => [k, String(v)])
      ).toString();
      const data = await api.get<ReceiptListResponse>(`/api/receipts${qs ? `?${qs}` : ""}`);
      setReceipts(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadReceipt = useCallback(async (file: File): Promise<Receipt> => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<Receipt>("/api/receipts", fd);
  }, []);

  const updateReceipt = useCallback(async (id: string, updates: Partial<Receipt>): Promise<Receipt> => {
    const updated = await api.put<Receipt>(`/api/receipts/${id}`, updates);
    setReceipts(prev => prev.map(r => (r.id === id ? updated : r)));
    return updated;
  }, []);

  const deleteReceipt = useCallback(async (id: string) => {
    await api.delete(`/api/receipts/${id}`);
    setReceipts(prev => prev.filter(r => r.id !== id));
    setTotal(prev => prev - 1);
  }, []);

  return { receipts, total, loading, fetchReceipts, uploadReceipt, updateReceipt, deleteReceipt };
}
